import { createSlice } from "@reduxjs/toolkit";
import {
  fetchUnreadSummaryThunk,
  markDisputeReadThunk,
} from "../thunks/disputeCommentReadsThunks";

// Server-driven unread state for dispute comment threads.
// Replaces the per-browser localStorage 'disputeChat:lastSeen:*' keys with a
// single Redux slice fed by REST (initial load) + SignalR (live deltas).
//
// Shape:
//   unreadByDisputeId: { [disputeId]: boolean }
//     - true  => latest comment by another user is newer than my last_read_at
//     - false => I've read everything (or no third-party comments exist)
//   latestCommentAtByDisputeId: { [disputeId]: ISO timestamp | null }
//     - Used by callers that want to display "last activity" alongside the dot.
//     - Updated from both the REST summary and incoming SignalR events.
const initialState = {
  unreadByDisputeId: {},
  latestCommentAtByDisputeId: {},
  loading: false,
  error: null,
};

const disputeCommentReadsSlice = createSlice({
  name: "disputeCommentReads",
  initialState,
  reducers: {
    // Called by the SignalR handler when "ReceiveDisputeComment" arrives.
    // We only flip the dot for comments authored by *someone else* — the
    // user's own comments must not light up their own dot.
    commentReceived(state, action) {
      const { disputeId, createdBy, createdAt, currentUserId } =
        action.payload || {};
      if (!disputeId) return;
      if (createdAt) {
        state.latestCommentAtByDisputeId[disputeId] = createdAt;
      }
      const isOwn =
        currentUserId != null &&
        createdBy != null &&
        String(currentUserId) === String(createdBy);
      if (!isOwn) {
        state.unreadByDisputeId[disputeId] = true;
      }
    },
    // Called by the SignalR "DisputeCommentRead" handler so that opening the
    // chat in tab A clears the dot in tab B.
    disputeMarkedRead(state, action) {
      const { disputeId } = action.payload || {};
      if (!disputeId) return;
      state.unreadByDisputeId[disputeId] = false;
    },
    // Drop everything on logout/user switch — otherwise stale unread state
    // from a previous session could bleed into the new session.
    clearUnread(state) {
      state.unreadByDisputeId = {};
      state.latestCommentAtByDisputeId = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadSummaryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnreadSummaryThunk.fulfilled, (state, action) => {
        state.loading = false;
        for (const row of action.payload || []) {
          if (!row?.disputeId) continue;
          state.unreadByDisputeId[row.disputeId] = Boolean(row.hasUnread);
          state.latestCommentAtByDisputeId[row.disputeId] =
            row.latestCommentAt || null;
        }
      })
      .addCase(fetchUnreadSummaryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markDisputeReadThunk.fulfilled, (state, action) => {
        const id = action.payload?.disputeId;
        if (id) state.unreadByDisputeId[id] = false;
      });
  },
});

export const { commentReceived, disputeMarkedRead, clearUnread } =
  disputeCommentReadsSlice.actions;
export default disputeCommentReadsSlice.reducer;
